// Product brief for OneAmerica AnnuityCare II (two-pool annuity hybrid). See
// cmTwo.ts for the format intent.

export const annuityCareTwoBrief = `**OneAmerica AnnuityCare II** — single-premium deferred annuity with a built-in
LTC Accumulated Value (LTCAV) plus a separately-priced Continuation of Benefits
(COB) rider (calculationType \`annuityHybrid\`, single-life only).

Pool model — **two real pools, drawn sequentially**:
- \`base_pool\` is the **LTCAV**, the first LTC pool. New uploads seed it from
  year-1 \`policyBaseBenefitAmountAtIssue\`, then derive scheduled LTCAV from
  the base-benefit guaranteed rates and monthly continuation charge. Rows with
  the at-issue field but an incomplete rate-model set fall back to
  \`policyMaximumBenefitAmount\` as a flat LTCAV. Monthly cap = scheduled LTCAV
  / 24 months, or \`policyAccelerationBenefitDurationMonths\` when supplied.
  **Pre-rate-model rows** (\`policyBaseBenefitAmountAtIssue\` null) are never
  reinterpreted (P2-29): the stored dollar is the old prompt's Total LTC
  Balance, the monthly cap comes from the daily-benefit fields (no pool ÷
  period derivation), and a \`DATA WARNING\` asks for a re-upload.
- \`eob\` is the **COB Balance**, the second LTC pool. New uploads seed it from
  year-1 \`policyContinuationBenefitAmountAtIssue\`; legacy rows fall back to
  \`policyContinuationBenefitAmount\`. It becomes available **only once the
  LTCAV reaches zero**. Monthly cap = scheduled COB / 72 months, or
  \`policyEobBenefitDurationMonths\` when supplied.
- For rate-model rows, COB uses \`policyContinuationBenefitFirstAnniversaryRate\`
  for the year-1 → year-2 step, then compounds by
  \`policyInflationProtectionOnCOBAmount\` after that. This reproduces the muted
  first anniversary step in the illustration instead of applying 5% immediately.
- At issue the two monthly caps are equal in the sample (LTCAV/24 = COB/72);
  they diverge as LTCAV follows its guaranteed rate schedule and COB follows its
  continuation schedule. Total LTC = scheduled LTCAV + scheduled COB.
- The \`db\` column carries the annuity's **Accumulated Value floor**, not a life
  death benefit. New uploads seed it from \`policyAccumulatedValueAtIssue\` and
  can derive claim-start AV from the accumulated-value guaranteed rates; legacy
  rows fall back to \`policyMinimumGuaranteedBenefitAmount\`. **Only LTCAV draws
  reduce it, on a proportional basis** (close AV = open AV × LTCAV-after /
  LTCAV-before, carrier p.3 — P2-30), so the AV reaches $0 exactly when the
  LTCAV pool exhausts; COB draws do NOT touch the AV. Pre-rate-model rows keep
  the legacy dollar-for-dollar clamp (their base pool is the old prompt's Total
  LTC Balance, not the LTCAV — P2-29). Any residual after the claim is the
  non-LTC payout (the finalize death benefit).

Payout model — reimbursement (default) or cash indemnity:
- **Reimbursement** (default): reimburses eligible cost up to each pool's daily
  cap, LTCAV first then COB. Care cost above the cap is out-of-pocket.
- **Indemnity** (election): pays the pro-rated cap as cash, LTCAV first then COB.

Elimination period:
- 90-day EP, days out-of-pocket (\`in-wp\` rows). No retroactive lump.

Ground truth — sample illustration (\`Annuity Care 2 w Inflation (2).pdf\`,
Male 60, CA, Non-Qualified, $100,000 Contract Premium = $54,930 Net Single
Premium + $45,070 for the 5% compound COB Inflation option):
- Year 1: **LTCAV $56,073** (24-mo period, monthly cap **$2,336.38** =
  56,073/24) + **COB $168,219** (72-mo period, monthly cap **$2,336.38** =
  168,219/72) = **Total LTC Balance $224,292**.
- Year 10: LTCAV $62,321 / cap $2,596.74; COB $255,314 / cap $3,546.03 —
  total $317,635. By yr55 the COB cap reaches $31,861 vs LTCAV's $3,257.
- COB Balance = COB monthly × 72 **exactly** at every year; COB monthly grows
  by the first-anniversary step once, then ×1.05 on later anniversaries.
- AV floor **$56,046** (year-1 Accumulated Value); 90-day elimination period.

Things to eyeball:
- Rate-model rows should show scheduled LTCAV and COB values by policy year;
  legacy rows without the required field set keep the old flat LTCAV fallback.
- The draw moves to COB only after the LTCAV hits $0 — watch the cap switch
  from the LTCAV cap to the scheduled COB cap on that row.
- Residual: when total LTCAV drawn is below the AV floor, a residual non-LTC
  payout = floor − LTCAV drawn surfaces; once LTCAV draws exceed the floor the
  residual is $0 and the \`db\` column sits at $0.
`;
